import { Breadcrumb } from 'react-breadcrumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { difference, find, isEmpty, omit, toPairs } from 'lodash';
import { faDoorOpen, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Flash from '../../library/utils/Flash';
import LoadingSpinner from '../../library/utils/LoadingSpinner';
import RoomsTable from './RoomsTable';
import Section from '../../library/layout/Section';
import SelectInput from '../../library/inputs/SelectInput';
import Tag from '../../library/data-display/Tag';
import TagEditDiffMessage, { DiffAction, DiffType } from '../../library/data-display/TagEditDiffMessage';
import TextInput from '../../library/inputs/TextInput';
import useSyncStateWithQuery from '../../../hooks/use-sync-state-with-query';
import { useRooms, useRoomTags, useUpdateRoom } from '../../../hooks/queries/rooms';

import type { ActionMeta } from 'react-select';
import type { ChangeEvent } from 'react';
import type { EditDiff } from '../../library/data-display/TagEditDiffMessage';
import type { OnChangeValue } from 'react-select/dist/declarations/src/types';
import type { Option } from '../../library/inputs/SelectInput/types';
import type { RoomTag } from '../../../types';
import type { UpdateRoomPayload } from '../../../hooks/queries/rooms';
import { correctPath } from 'libraries/gem';

const Rooms = () => {
  const [search, querySearch, setSearch] = useSyncStateWithQuery<string>('q', '', { delay: 200 });
  const [showArchived, , setShowArchived] = useSyncStateWithQuery<string>('show_archived', 'false');
  const [tag, queryTag, setTag] = useSyncStateWithQuery<string[]>('tag', [], { array: true });
  const [pageNumber, queryPageNumber, setPageNumber] = useSyncStateWithQuery<string>('page', '1');

  const { data: roomTags } = useRoomTags();
  const tagOptionsFromServer = useMemo<Option<string>[]>(() => (roomTags || []).map((tag) => ({
    label: tag.id,
    value: tag.id,
  })), [roomTags]);
  const [tagOptions, setTagOptions] = useState<Option<string>[]>(tagOptionsFromServer);

  const {
    data: rooms,
    error: roomsError,
    isFetching: isRoomsFetching,
  } = useRooms({
    archived: showArchived === 'true',
    name: querySearch, // This has to be querySearch so it doesn't make a lot of requests when you type fast.
    room_tag: queryTag,
    limit: 10,
    offset: 10 * (parseInt(queryPageNumber, 10) - 1),
  });

  const [isFetching, setIsFetching] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [roomPayloads, setRoomPayloads] = useState<Record<string, UpdateRoomPayload & { name: string }>>({});
  const [editDiff, setEditDiff] = useState<EditDiff>({});
  const [errors, setErrors] = useState<string[]>([]);

  const updateRoomMutation = useUpdateRoom();

  useEffect(() => {
    setTagOptions(tagOptionsFromServer);
  }, [JSON.stringify(tagOptionsFromServer)]);

  useEffect(() => {
    const newTagOptions = [...tagOptions];

    // We need to keep track of whether it changed, otherwise, we'd always call
    // setTagOptions which would make this useEffect run again, causing an
    // infinite loop.
    let changed = false;

    tag.forEach((tag) => {
      if (find(tagOptions, ['value', tag])) {
        return;
      }

      changed = true;
      newTagOptions.push({ label: tag, value: tag });
    });

    if (changed) {
      setTagOptions(newTagOptions);
    }
  }, [JSON.stringify(tagOptions), tag]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (parseInt(pageNumber, 10) > 1) {
      setPageNumber('1', { method: 'replace' });
    }
  }, [pageNumber]);

  const handleShowArchivedChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setShowArchived(JSON.stringify(e.target.checked));
  }, []);

  const handleTagChange = useCallback((option: OnChangeValue<Option<string>, true>) => {
    setTag(option ? option.map((tag) => tag.value) : []);
    if (parseInt(pageNumber, 10) > 1) {
      setPageNumber('1', { method: 'replace' });
    }
  }, [pageNumber]);

  const handlePageNumberChange = useCallback((value: number) => {
    setPageNumber(value.toString());
  }, []);

  const handleTagsChange = (option: OnChangeValue<Option<string>, true>, actionMeta: ActionMeta<Option<string>>) => {
    const roomId = actionMeta.name;
    const room = find(rooms?.rooms, ['id', roomId]);
    if (!roomId || !room) {
      return;
    }

    if (actionMeta.action === 'create-option') {
      // update our complete list of tags to include this new one
      setTagOptions([
        ...tagOptions,
        { label: option[option.length - 1].label, value: option[option.length - 1].value },
      ]);
    }

    // update the edit diff appropriately
    const previousValues = (roomPayloads[roomId]?.room_tags || room.room_tags || []).map((tag) => (tag as RoomTag).id || tag as string);
    const newValues = option ? option.map((tag) => tag.value) : [];
    const newEditDiff: EditDiff = { ...editDiff };

    difference(previousValues, newValues).map((removed) => {
      if (newEditDiff[`tag:added:${removed}`]?.items[roomId]) {
        // this tag that was just removed was recently added, so instead
        // of saying it was added and removed, just delete the addition
        delete newEditDiff[`tag:added:${removed}`].items[roomId];
        // if this was the last room to have this tag added, delete the
        // entry in the diff for it so it doesn't show an empty block in the
        // diff flash message
        if (isEmpty(newEditDiff[`tag:added:${removed}`].items)) {
          delete newEditDiff[`tag:added:${removed}`];
        }
      } else {
        // record that this tag is being removed from this room
        newEditDiff[`tag:removed:${removed}`] = {
          ...newEditDiff[`tag:removed:${removed}`] || {
            type: DiffType.Tag,
            action: DiffAction.Removed,
            value: removed,
          },
          items: {
            ...newEditDiff[`tag:removed:${removed}`]?.items || {},
            [roomId]: room.name,
          },
        };
      }
    });
    difference(newValues, previousValues).map((added) => {
      if (newEditDiff[`tag:removed:${added}`]?.items[roomId]) {
        // this tag that was just added was recently removed, so instead
        // of saying it was removed and added, just delete the addition
        delete newEditDiff[`tag:removed:${added}`].items[roomId];
        // if this was the last room to have this tag removed, delete
        // the entry in the diff for it so it doesn't show an empty block in the
        // diff flash message
        if (isEmpty(newEditDiff[`tag:removed:${added}`].items)) {
          delete newEditDiff[`tag:removed:${added}`];
        }
      } else {
        // record that this tag is being added to this room
        newEditDiff[`tag:added:${added}`] = {
          ...newEditDiff[`tag:added:${added}`] || {
            type: DiffType.Tag,
            action: DiffAction.Added,
            value: added,
          },
          items: {
            ...newEditDiff[`tag:added:${added}`]?.items || {},
            [roomId]: room.name,
          },
        };
      }
    });

    setEditDiff(newEditDiff);
    setRoomPayloads((prevState) => ({
      ...prevState,
      [roomId]: {
        ...prevState[roomId] || {},
        // we're saving the name here so we can use it for any potential error
        // messages
        name: room.name,
        room_tags: newValues,
      },
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setIsSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsSuccess(false);
    setTagOptions(tagOptionsFromServer);
    setEditDiff({});
    setRoomPayloads({});
  };

  const handleSave = async () => {
    setIsFetching(true);
    setIsSuccess(false);

    const responses = await Promise.all(
      toPairs(roomPayloads).map(
        async ([roomId, payload]) => {
          try {
            return await updateRoomMutation.mutateAsync({ id: roomId, payload: omit(payload, ['name']) });
          } catch (err) {
            return { id: roomId, error: err as Error };
          }
        }
      )
    );

    const errors = responses
    .filter((resp: any): resp is { id: string; error: Error } => Boolean(resp.error))
    .map(({ id, error }) => `${roomPayloads[id].name}: ${error.message}`);

    if (errors.length > 0) {
      setErrors(errors);
      setIsFetching(false);
      return;
    }

    // if there were errors, we don't want to clear out the payloads
    setEditDiff({});
    setRoomPayloads({});

    setIsFetching(false);
    setIsSuccess(true);
    setIsEditing(false);
  };

  const selectedTags = useMemo<Option<string>[]>(() => {
    if (!tag) {
      return [];
    }
    return tag
    .map((tag) => find(tagOptions, ['value', tag]))
    .filter((t): t is Option<string> => Boolean(t));
  }, [tag, tagOptions]);

  return (
    <Breadcrumb
      data={{
        title: <><FontAwesomeIcon icon={faDoorOpen} />Rooms</>,
        pathname: correctPath('/app/rooms'),
      }}
    >
      <div className="rooms-container">
        <Helmet>
          <title>Rooms | Gem Scheduling</title>
        </Helmet>
        <Section
          isEditable
          isEditing={isEditing}
          isSaving={isFetching}
          onCancel={handleCancel}
          onEdit={handleEdit}
          onSave={handleSave}
          title="Rooms"
        >
          <Flash
            isDismissible
            message="Successfully updated!"
            showFlash={isSuccess}
            type="success"
          />
          <Flash
            message={<TagEditDiffMessage editDiff={editDiff} />}
            showFlash={!isEmpty(editDiff)}
            type="info"
          />
          <Flash
            message={JSON.stringify(errors)}
            showFlash={!isEmpty(errors) && isEditing}
            type="danger"
          />
          <div className="filters-container">
            <TextInput
              className="text-input-rooms-filter"
              isAutofocus
              label="Search"
              leftIcon={isRoomsFetching ? <LoadingSpinner /> : <FontAwesomeIcon icon={faSearch} />}
              onChange={handleSearchChange}
              placeholder="Room name"
              value={search}
            />
            <SelectInput
              className="select-input-rooms-tag"
              formatOptionLabel={(option) => (
                <Tag
                  isNegated={false}
                  type="tag"
                  value={option.value}
                />
              )}
              isClearable
              isMulti
              label="Tag"
              onChange={handleTagChange}
              options={tagOptions}
              value={selectedTags}
            />
          </div>
          <Flash
            message={roomsError?.message}
            showFlash={Boolean(roomsError)}
            type="danger"
          />
          {rooms &&
            <RoomsTable
              isEditing={isEditing || isFetching}
              onPageNumberChange={handlePageNumberChange}
              onShowArchivedChange={handleShowArchivedChange}
              onTagsChange={handleTagsChange}
              pageNumber={parseInt(pageNumber, 10)}
              roomPayloads={roomPayloads}
              rooms={rooms.rooms}
              showArchived={showArchived === 'true'}
              tagOptions={tagOptions}
              totalCount={rooms.total}
            />
          }
        </Section>
      </div>
    </Breadcrumb>
  );
};

export default Rooms;
